import { useState } from "react";
import { ImageIcon } from "lucide-react";
import { UploadDropzone } from "./UploadDropzone";
import { SourceModeToggle, FaithfulToggle } from "./SourceModeToggle";
import { RenderPickers } from "./RenderPickers";
import { LogoUploader } from "./LogoUploader";
import { GenerateButton } from "./GenerateButton";
import { FieldLabel } from "./FieldLabel";
import { StyleMatchControl } from "./StyleMatchControl";
import { useStyleMatch } from "../hooks/useStyleMatch";
import { useAuth } from "../context/AuthContext";

const DuckNameInput = ({ value, onChange }) => (
  <div>
    <FieldLabel color="text-accent">Name your duck (optional)</FieldLabel>
    <input
      data-testid="duck-name-input"
      value={value}
      maxLength={60}
      onChange={(e) => onChange(e.target.value)}
      placeholder="e.g. Captain Quackbeard — leave blank and Duckify names it"
      className="w-full h-12 rounded-full border border-border bg-background px-5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
    />
  </div>
);

// Step 1: the source image (photo or finished render), duck name, style/figure pickers, optional logo.
export const PhotoUploadDropzone = ({ onGenerate, loading }) => {
  const [mode, setMode] = useState("photo");
  const [useLibrary, setUseLibrary] = useState(true);
  const { user, sessionReady } = useAuth();
  const match = useStyleMatch(user?.id, sessionReady);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [style, setStyle] = useState("classic");
  const [name, setName] = useState("");
  const [logo, setLogo] = useState(null);
  const [placement, setPlacement] = useState("none");
  const [faithful, setFaithful] = useState(true);   // uploaded renders are reproduced exactly by default

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const fromPhoto = mode === "photo";
  const canGenerate = !!file && !loading && !match.pending;
  const generate = () =>
    onGenerate({ file, style, figure: "full", name, logo: fromPhoto ? logo : null, logoPlacement: placement, asRender: !fromPhoto, useLibrary, styleRefCount: match.count, faithful: fromPhoto ? false : faithful });

  return (
    <div className="grid lg:grid-cols-[1.15fr_1fr] gap-8 items-start">
      <div className="space-y-6">
        <UploadDropzone mode={mode} previewUrl={previewUrl} onFile={pickFile} />
        <DuckNameInput value={name} onChange={setName} />
      </div>

      <div className="space-y-6">
        <SourceModeToggle value={mode} onChange={setMode} />
        {!fromPhoto && <FaithfulToggle value={faithful} onChange={setFaithful} />}
        <RenderPickers style={style} onStyle={setStyle} showStyle={fromPhoto} />
        {fromPhoto && <StyleMatchControl enabled={useLibrary} onEnabled={setUseLibrary} count={match.count} onChange={match.choose} disabled={loading || match.pending} error={match.error} />}
        {fromPhoto && <LogoUploader logo={logo} placement={placement} onLogo={setLogo} onPlacement={setPlacement} />}
        <GenerateButton fromPhoto={fromPhoto} loading={loading} disabled={!canGenerate} onClick={generate} />
        <p className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
          <ImageIcon className="h-3.5 w-3.5" /> Render by Duckify from Zero Ducks Given
        </p>
      </div>
    </div>
  );
};
